import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { GtmService } from '@fcom/common/gtm';
import { ButtonSize, ButtonTheme, NotificationTheme, IconPosition } from '@fcom/ui-components';
import { BookingAppState, MultiCityState, OffersPerBound, MultiCityStatus } from '@fcom/common/interfaces/booking';
import {
  ElementActions,
  GTtmMulticityTicketTypeOptions,
  GaContext,
  LoaderType,
  OfferListFetchParams,
} from '@fcom/common/interfaces';
import { unsubscribe } from '@fcom/core/utils';
import { PaxAmount } from '@fcom/dapi';
import { GlobalBookingFlight, globalBookingFlights, globalBookingPaxAmount } from '@fcom/common/store';
import { ScrollService } from '@fcom/common/services';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';
import { Bound } from '@fcom/dapi/api/models';
import { ExtendedFareFamily } from '@fcom/common-booking/interfaces';

import { BookingMultiCityService } from '../../services';
import {
  selectMultiCityAmountOfBounds,
  selectMultiCity,
  selectMultiCitySelectedItinerary,
  selectMultiCityStatus,
  selectMultiCityFareFamiliesPerCabin,
  selectMultiCityAvailableFareFamilies,
} from '../../../../store/selectors';
import { MultiCityActions } from '../../../../store/actions';
import { BookingService } from '../../../../services';
import { setMultiCitySelectedItinerary } from '../../../../store/actions/multi-city.actions';

export enum MultiCityStep {
  FARE_FAMILY,
  BOUND_1,
  BOUND_2,
  BOUND_3,
  BOUND_4,
  BOUND_5,
  BOUND_6,
}

@Component({
  selector: 'fin-multi-city-view',
  styleUrls: ['./multi-city-view.component.scss'],
  templateUrl: './multi-city-view.component.html',
})
export class MultiCityViewComponent implements OnInit, OnDestroy {
  @Input({ required: true }) flightSearch$: Observable<OfferListFetchParams>;

  public MultiCityStep = MultiCityStep;
  public LoaderType = LoaderType;

  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonSize = ButtonSize;
  public readonly IconPosition = IconPosition;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  private subscriptions = new Subscription();

  step$ = new BehaviorSubject(MultiCityStep.FARE_FAMILY);

  MultiCityStatus = MultiCityStatus;
  NotificationTheme = NotificationTheme;
  offersStatus$: Observable<MultiCityStatus>;
  offers$: Observable<MultiCityState>;
  flights$: Observable<GlobalBookingFlight[]>;
  paxAmount$: Observable<PaxAmount>;
  currencyCode$: Observable<string>;
  amountOfBounds$: Observable<number>;
  selectedItinerary$: Observable<string[]>;

  expandOpen = false;

  constructor(
    private store$: Store<BookingAppState>,
    private bookingMultiCityService: BookingMultiCityService,
    private scrollService: ScrollService,
    private bookingService: BookingService,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.flights$ = this.store$.pipe(globalBookingFlights());
    this.offersStatus$ = this.store$.pipe(select(selectMultiCityStatus));
    this.offers$ = this.store$.pipe(select(selectMultiCity), filter(Boolean));
    this.currencyCode$ = this.offers$.pipe(map((offers) => offers.currency));
    this.paxAmount$ = this.store$.pipe(globalBookingPaxAmount());
    this.selectedItinerary$ = this.store$.pipe(select(selectMultiCitySelectedItinerary));
    this.amountOfBounds$ = this.store$.pipe(select(selectMultiCityAmountOfBounds), filter(Boolean));
    this.scrollService.scrollTop();

    this.subscriptions.add(
      combineLatest([
        this.flightSearch$,
        this.store$.pipe(select(selectMultiCityFareFamiliesPerCabin)),
        this.store$.pipe(select(selectMultiCityAvailableFareFamilies)),
        this.offersStatus$.pipe(
          filter((status) => status === MultiCityStatus.READY),
          take(1)
        ),
      ])
        .pipe(take(1))
        .subscribe(([flightSearch, fareFamiliesPerCabin, availableFareFamilies]) => {
          const fareFamilyCodesByBookingClass: GTtmMulticityTicketTypeOptions = availableFareFamilies.reduce(
            (acc, detail) => {
              const fareFamilyCode = detail.fareFamilyCode;
              const bookingClass = Object.keys(fareFamiliesPerCabin).find((key) =>
                fareFamiliesPerCabin[key].includes(fareFamilyCode)
              );

              if (bookingClass) {
                const lowerCaseBookingClass = bookingClass.toLowerCase();
                return {
                  ...acc,
                  [lowerCaseBookingClass]: acc[lowerCaseBookingClass]
                    ? `${acc[lowerCaseBookingClass]}_${fareFamilyCode}`
                    : fareFamilyCode,
                };
              }

              return acc;
            },
            {
              economy: '',
              ecopremium: '',
              business: '',
            } as GTtmMulticityTicketTypeOptions
          );

          this.gtmService.multicityTicketTypeOptions(flightSearch.cabin, fareFamilyCodesByBookingClass);
        })
    );

    this.subscriptions.add(
      this.offersStatus$
        .pipe(
          filter((status) => {
            return [MultiCityStatus.ERROR, MultiCityStatus.NO_OFFERS, MultiCityStatus.READY].includes(status);
          }),
          take(1)
        )
        .subscribe((status) => {
          this.gtmService.trackElement(
            'multicity-search-results',
            GaContext.BOOKING_FLOW,
            undefined,
            status,
            ElementActions.VIEW
          );
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  continueToBoundsStep(fareFamily: ExtendedFareFamily): void {
    this.store$.dispatch(
      MultiCityActions.setMultiCitySelectedFareFamily({
        fareFamily: fareFamily.fareFamilyCode,
      })
    );
    this.step$.next(MultiCityStep.BOUND_1);
  }

  continueToNextBound(offer: OffersPerBound): void {
    this.store$.dispatch(
      MultiCityActions.addMultiCitySelectedItinerary({
        itinerary: offer.itineraryId,
      })
    );

    this.subscriptions.add(
      combineLatest([this.selectedItinerary$, this.amountOfBounds$])
        .pipe(take(1))
        .subscribe(([selectedItinerary, amountOfBounds]) => {
          if (selectedItinerary.length === amountOfBounds) {
            this.selectOffer(offer);
          } else {
            this.step$.next(this.step$.getValue() + 1);
            this.scrollService.scrollTop();
          }
        })
    );
  }

  selectOffer(offer: OffersPerBound): void {
    this.bookingMultiCityService.createCart(offer.cheapestOffer);
    this.bookingService.setStep({ step: BOOKING_STEPS.PAXDETAILS });
    this.store$.dispatch(MultiCityActions.reset());
  }

  resetSelection(): void {
    this.store$.dispatch(MultiCityActions.resetSelections());
    this.step$.next(MultiCityStep.FARE_FAMILY);
  }

  resetToBound({ index }: { bound: Bound; index: number }): void {
    this.subscriptions.add(
      this.selectedItinerary$.pipe(take(1)).subscribe((itinerary) => {
        this.store$.dispatch(
          setMultiCitySelectedItinerary({
            itinerary: itinerary.slice(0, index),
          })
        );
        this.step$.next(index + 1);
      })
    );
  }

  onTryAgain(): void {
    this.bookingService.setStep({
      step: BOOKING_STEPS.ENTRY,
    });
  }
}
